import type {
  AuditEvent, ChangeStatus, Comment, CommentAnchor, DocumentRecord, Draft, FeedbackExport, GuestInfo, ID, NotificationPrefs, NotificationSettings, Org, Project,
  ProposedChange, Role, ShareLinkSummary, Snapshot, Story, Thread, ThreadAttachment, TourId, User,
} from '@proofroom/schema';
import { api, ApiError } from './client';

export type DocumentWithDrafts = DocumentRecord & { drafts: Draft[] };

/**
 * The server resolves every author id it returns to a name and role, with no email, so a client
 * can render people without the admin-only user directory. See the Authorship section of
 * docs/API.md. The reference is optional because a user may since have been deleted.
 */
import type { UserRef } from '@proofroom/schema';
export type { UserRef };

export type ResolvedComment = Comment & { author?: UserRef };
export type ResolvedThread = Omit<Thread, 'comments'> & { comments: ResolvedComment[]; createdByUser?: UserRef; resolvedByUser?: UserRef };
export type ResolvedChange = ProposedChange & { author?: UserRef; decidedByUser?: UserRef };
export type ResolvedAuditEvent = AuditEvent & { actor?: UserRef };

/** Another author's still-proposed change on a paragraph a send touched: reported, not superseded */
export interface ChangeConflict { paraId: ID; changeId: ID; authorName: string }
export interface SendChangesResult { created: ResolvedChange[]; conflicts: ChangeConflict[] }

export const auth = {
  login: (email: string, password: string) => api<User>('/api/auth/login', { body: { email, password } }),
  logout: () => api<{ ok: true }>('/api/auth/logout', { method: 'POST', body: {} }),
  me: () => api<User>('/api/auth/me'),
  methods: () => api<{ password: boolean; entra: boolean }>('/api/auth/methods'),
  reset: (token: string, password: string) => api<{ ok: true }>('/api/auth/reset', { body: { token, password } }),
};

export const projects = {
  list: () => api<Project[]>('/api/projects'),
  documents: (projectId: ID) => api<DocumentRecord[]>(`/api/projects/${projectId}/documents`),
  create: (name: string, clientOrgId?: ID) => api<Project>('/api/projects', { body: { name, ...(clientOrgId ? { clientOrgId } : {}) } }),
  details: (id: ID, patch: { clientOrgId?: ID | null; beatJobNumber?: string; beatJobUrl?: string }) => api<Project>(`/api/projects/${id}`, { method: 'PATCH', body: patch }),
  update: (id: ID, name: string) => api<Project>('/api/projects/' + id, { method: 'PATCH', body: { name } }),
  remove: (id: ID) => api<{ ok: true }>('/api/projects/' + id + '?withDocuments=true', { method: 'DELETE' }),
};

/** One paragraph a draft-to-draft diff found different: text only, matched by paragraph id. */
export interface DraftDiffParagraph { id: ID; kind: 'changed' | 'added' | 'removed'; before?: string; after?: string }
export interface DraftDiffStory { storyId: ID; label?: string; paragraphs: DraftDiffParagraph[]; sameCount: number }
export interface DraftDiffResult { from: { id: ID; number: number }; to: { id: ID; number: number }; stories: DraftDiffStory[] }

export const documents = {
  remove: (id: ID) => api<{ ok: true }>(`/api/documents/${id}`, { method: 'DELETE' }),
  tags: (id: ID, tags: string[]) => api<DocumentRecord>(`/api/documents/${id}`, { method: 'PATCH', body: { tags } }),
  get: (id: ID) => api<DocumentWithDrafts>(`/api/documents/${id}`),
  move: (id: ID, projectId: ID) => api<DocumentRecord>(`/api/documents/${id}`, { method: 'PATCH', body: { projectId } }),
  drafts: (id: ID) => api<Draft[]>(`/api/documents/${id}/drafts`),
  changes: (id: ID, status?: ChangeStatus) => api<ResolvedChange[]>(`/api/documents/${id}/changes${status ? `?status=${status}` : ''}`),
  threads: (id: ID, draftId?: ID) => api<ResolvedThread[]>(`/api/documents/${id}/threads${draftId ? `?draftId=${draftId}` : ''}`),
  createThread: (id: ID, draftId: ID, anchor: CommentAnchor, body: string) => api<ResolvedThread>(`/api/documents/${id}/threads`, { body: { draftId, anchor, body } }),
  audit: (id: ID, limit = 100, before?: string) => api<ResolvedAuditEvent[]>(`/api/documents/${id}/audit?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ''}`),
  pluginStatus: (id: ID) => api<{ connected: boolean; userName?: string; lastSeenAt?: string }>(`/api/documents/${id}/plugin-status`),
  diff: (id: ID, from: ID, to: ID) => api<DraftDiffResult>(`/api/documents/${id}/diff?from=${from}&to=${to}`),
  feedback: (id: ID) => api<FeedbackExport>(`/api/documents/${id}/feedback`),
  /** A plain link, not a fetch: the browser saves the CSV itself, cookie and all. */
  feedbackCsvUrl: (id: ID) => `/api/documents/${id}/feedback.csv`,
};

export interface PreviewPdfBody { scope: 'spread' | 'document'; spread?: number }
export interface PreviewPdfResult { blob: Blob; filename: string }

const FILENAME_RE = /filename="?([^";]+)"?/i;

export const drafts = {
  get: (id: ID) => api<Draft>(`/api/drafts/${id}`),
  snapshot: (id: ID) => api<Snapshot>(`/api/drafts/${id}/snapshot`),
  /** The draft's stories with any unsent room edits applied, as the print route prints them. */
  currentStories: (id: ID) => api<Story[]>(`/api/drafts/${id}/stories/current`),
  /** The draft's stories with the changes the designer has accepted or applied folded in. */
  effectiveStories: (id: ID) => api<Story[]>(`/api/drafts/${id}/stories/effective`),
  sendChanges: (id: ID, stories: Story[]) => api<SendChangesResult>(`/api/drafts/${id}/changes/send`, { body: { stories } }),
  approve: (id: ID) => api<Draft>(`/api/drafts/${id}/approve`, { body: {} }),
  unapprove: (id: ID) => api<Draft>(`/api/drafts/${id}/unapprove`, { body: {} }),
  /** Fetches directly rather than through api(): the success response is a PDF blob, not JSON. */
  previewPdf: async (id: ID, body: PreviewPdfBody): Promise<PreviewPdfResult> => {
    const res = await fetch(`/api/drafts/${id}/preview.pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let code = 'http_error';
      let message = `${res.status} ${res.statusText}`;
      try {
        const j = (await res.json()) as { error?: { code?: string; message?: string } };
        if (j?.error) { code = j.error.code ?? code; message = j.error.message ?? message; }
      } catch { /* not JSON */ }
      throw new ApiError(res.status, code, message);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const filename = FILENAME_RE.exec(disposition)?.[1] ?? 'preview.pdf';
    return { blob: await res.blob(), filename };
  },
};

/** One id's outcome from a batch decide: a failed item carries the server's HttpError code. */
export interface BatchDecisionResult { id: ID; ok: boolean; error?: string }
export interface BatchDecisionOutcome { results: BatchDecisionResult[] }

export const changes = {
  accept: (id: ID, note?: string) => api<ResolvedChange>(`/api/changes/${id}/accept`, { body: note ? { note } : {} }),
  reject: (id: ID, note: string) => api<ResolvedChange>(`/api/changes/${id}/reject`, { body: { note } }),
  withdraw: (id: ID) => api<ResolvedChange>(`/api/changes/${id}/withdraw`, { body: {} }),
  decideBatch: (ids: ID[], decision: 'accepted' | 'rejected', note?: string) =>
    api<BatchDecisionOutcome>('/api/changes/decide-batch', { body: note ? { ids, decision, note } : { ids, decision } }),
};

const attachmentsPath = (documentId: ID, threadId: ID): string => `/api/documents/${documentId}/threads/${threadId}/attachments`;
/** Where a thread's attachment downloads from: an <img src> or a plain link, both work unauthenticated by the browser sending the session cookie itself. */
export const attachmentUrl = (documentId: ID, threadId: ID, attachmentId: ID): string => `${attachmentsPath(documentId, threadId)}/${attachmentId}`;

export const threads = {
  question: (id: ID, needsAnswer: boolean) => api<ResolvedThread>(`/api/threads/${id}/question`, { method: 'PATCH', body: { needsAnswer } }),
  comment: (id: ID, body: string) => api<ResolvedComment>(`/api/threads/${id}/comments`, { body: { body } }),
  resolve: (id: ID) => api<ResolvedThread>(`/api/threads/${id}/resolve`, { method: 'POST', body: {} }),
  reopen: (id: ID) => api<ResolvedThread>(`/api/threads/${id}/reopen`, { method: 'POST', body: {} }),
  /** Fetches directly rather than through api(): the body is FormData, not JSON. */
  uploadAttachment: async (documentId: ID, threadId: ID, file: File): Promise<ThreadAttachment> => {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await fetch(attachmentsPath(documentId, threadId), { method: 'POST', credentials: 'include', body: form });
    if (!res.ok) {
      let code = 'http_error';
      let message = `${res.status} ${res.statusText}`;
      try {
        const j = (await res.json()) as { error?: { code?: string; message?: string } };
        if (j?.error) { code = j.error.code ?? code; message = j.error.message ?? message; }
      } catch { /* not JSON */ }
      throw new ApiError(res.status, code, message);
    }
    return (await res.json()) as ThreadAttachment;
  },
  deleteAttachment: (documentId: ID, threadId: ID, attachmentId: ID) =>
    api<{ ok: true }>(attachmentUrl(documentId, threadId, attachmentId), { method: 'DELETE' }),
};

export const plugin = {
  version: () => api<{ version: string; sha: string; builtAt: string }>('/api/plugin/version'),
};

export type UserWithProjects = User & { projectIds: ID[]; orgName: string };

export interface CreateUserInput { email: string; name: string; role: Role; password: string; projectIds: ID[]; orgId?: ID }
export interface UpdateUserPatch { name?: string; role?: Role; projectIds?: ID[]; orgId?: ID }

/** What an admin may set on another user's alerts: either half alone leaves the other as it was */
export interface AdminNotificationPatch { emailEnabled?: boolean; mutes?: Array<{ projectId: ID; muted: boolean }> }

export const users = {
  list: () => api<UserWithProjects[]>('/api/users'),
  create: (input: CreateUserInput) => api<User>('/api/users', { body: input }),
  update: (id: ID, patch: UpdateUserPatch) => api<User>(`/api/users/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: ID) => api<{ ok: true }>(`/api/users/${id}`, { method: 'DELETE' }),
  setPassword: (id: ID, password: string) => api<{ ok: true }>(`/api/users/${id}/password`, { body: { password } }),
  sendReset: (id: ID) => api<{ sent: boolean }>(`/api/users/${id}/reset-link`, { method: 'POST', body: {} }),
  notifications: (id: ID) => api<NotificationSettings>(`/api/users/${id}/notifications`),
  saveNotifications: (id: ID, patch: AdminNotificationPatch) => api<NotificationSettings>(`/api/users/${id}/notifications`, { method: 'PUT', body: patch }),
};

export const orgs = {
  list: () => api<Org[]>('/api/orgs'),
  create: (name: string) => api<Org>('/api/orgs', { body: { name } }),
};

export const pair = {
  approve: (code: string) => api<{ ok: true }>('/api/pair/approve', { body: { code } }),
};

/**
 * settings() is the effective map (the defaults for this user with their own choices over them),
 * the master switch, and a row per project they are on. Every write answers with the whole thing.
 */
export const notifications = {
  settings: () => api<NotificationSettings>('/api/notifications/prefs'),
  save: (patch: { prefs?: NotificationPrefs; emailEnabled?: boolean }) => api<NotificationSettings>('/api/notifications/prefs', { body: patch }),
  setProjectMuted: (projectId: ID, muted: boolean) => api<NotificationSettings>(`/api/notifications/projects/${projectId}`, { method: 'PUT', body: { muted } }),
};

/** Records that the signed-in user finished or skipped a tour; answers with their whole map, as /me carries it */
export const tours = {
  seen: (tour: TourId) => api<Record<string, string>>('/api/me/tours', { body: { tour } }),
};

/** What the browser needs to subscribe: the key, and whether the server can push at all */
export interface VapidKey { enabled: boolean; publicKey?: string }

export interface PushSubscriptionBody { endpoint: string; keys: { p256dh: string; auth: string } }

export const push = {
  vapidKey: () => api<VapidKey>('/api/push/vapid-key'),
  subscribe: (subscription: PushSubscriptionBody) => api<{ ok: true }>('/api/push/subscribe', { body: subscription }),
  unsubscribe: (endpoint: string) => api<{ ok: true }>('/api/push/subscribe', { method: 'DELETE', body: { endpoint } }),
};

/** One nightly run written by deploy/backup.sh, newest first from the server */
export interface BackupRun { id: string; startedAt: string; finishedAt: string; dbBytes: number; dataBytes: number; ok: boolean; error: string | null }

export const backups = {
  list: () => api<BackupRun[]>('/api/admin/backups'),
};

/** One document's files on disk, and what retention would prune under the server's configured keep */
export interface DocumentStorage {
  documentId: ID;
  projectId: ID;
  name: string;
  drafts: number;
  pruned: number;
  bytes: number;
  /** Draft numbers the plan names; always empty while `keep` is 0 */
  wouldPrune: number[];
}

/** `keep` is RETENTION_KEEP_DRAFTS: 0 means retention is off and nothing will ever be pruned */
export interface StorageReport { keep: number; documents: DocumentStorage[] }

/** One draft the server could not prune, named beside the ones it did */
export interface PruneFailure { number: number; error: string }

export const storage = {
  report: () => api<StorageReport>('/api/admin/storage'),
  /** `expect` is the draft numbers the caller was shown: a plan that has moved since is refused with a 409. */
  prune: (documentId: ID, expect: number[]) =>
    api<{ pruned: number[]; failed: PruneFailure[] }>('/api/admin/storage/prune', { body: { documentId, expect } }),
};

export interface TokenInfo { id: ID; label: string; createdAt: string; lastUsedAt?: string; expiresAt?: string }

export const tokens = {
  list: () => api<TokenInfo[]>('/api/auth/tokens'),
  remove: (id: ID) => api<{ ok: true }>(`/api/auth/tokens/${id}`, { method: 'DELETE' }),
};

/** What GET /api/share/:token says: the link's state, what it opens while it is open, and the caller's own guest if their cookie came through it */
export interface ShareState {
  state: 'ok' | 'expired' | 'revoked' | 'unknown';
  documentName?: string;
  projectName?: string;
  label?: string;
  guest: GuestInfo | null;
}

export interface CreateShareLinkInput { label: string; password: string; expiresInDays?: 7 | 30 | 90 }
/** The url carries the token, and this response is the only place it is ever shown */
export interface CreatedShareLink { link: ShareLinkSummary; url: string }

export const share = {
  /** An unknown token is a 404 whose body still says state unknown; the screen treats it as a state, not a failure */
  state: async (token: string): Promise<ShareState> => {
    try {
      return await api<ShareState>(`/api/share/${encodeURIComponent(token)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return { state: 'unknown', guest: null };
      throw err;
    }
  },
  unlock: (token: string, name: string, password: string) => api<{ guest: GuestInfo }>(`/api/share/${encodeURIComponent(token)}/unlock`, { body: { name, password } }),
  leave: () => api<{ ok: true }>('/api/share/leave', { method: 'POST', body: {} }),
  list: (documentId: ID) => api<ShareLinkSummary[]>(`/api/documents/${documentId}/share-links`),
  create: (documentId: ID, input: CreateShareLinkInput) => api<CreatedShareLink>(`/api/documents/${documentId}/share-links`, { body: input }),
  revoke: (id: ID) => api<ShareLinkSummary>(`/api/share-links/${id}/revoke`, { method: 'POST', body: {} }),
};

export interface BeatJob { uuid: string; number: string; name: string; clientUuid: string | null; clientName: string | null; status: string | null; syncedAt: string | null }
export interface BeatJobPage { jobs: BeatJob[]; total: number; page: number; limit: number }
export const beat = {
  status: () => api<{ enabled: boolean }>('/api/beat/status'),
  jobs: (query: string, page = 0) => api<BeatJobPage>(`/api/beat/jobs?${new URLSearchParams({ query, page: String(page) })}`),
  createProject: (jobUuid: string) => api<Project>('/api/beat/projects', { body: { jobUuid } }),
  link: (id: ID, jobUuid: string) => api<Project>(`/api/projects/${id}/beat`, { method: 'PUT', body: { jobUuid } }),
  backlink: (id: ID) => api<{ ok: true; alreadyAdded: boolean }>(`/api/projects/${id}/beat/backlink`, { body: {} }),
};
